import type { MemoryLayoutSpec, MemorySegment, ResolvedFigure, ShapeRole } from '../model.js';
import { box, finishFigure, point, SceneBuilder, validateViewport } from '../scene-builder.js';

const STRIP_TOP = 112;
const STRIP_HEIGHT = 72;

export function memorySegmentId(segmentId: string): string {
  return `memory.segment.${segmentId}`;
}

export function memoryOffsetId(offset: number): string {
  return `memory.offset.${offset}`;
}

/**
 * Compiles a coordinate-free struct description into an orthographic byte map.
 * Segment geometry is proportional to byte size; the ledger below the map is
 * deliberately regular so a one-byte field still has a full touch target.
 */
export function compileMemoryLayout(spec: MemoryLayoutSpec): ResolvedFigure {
  validateMemoryLayout(spec);

  const builder = new SceneBuilder();
  const { width, height } = spec.viewport;
  const narrow = width <= 390;
  const margin = width <= 340 ? 12 : Math.max(18, Math.min(34, width * 0.055));
  const stripX = margin;
  const stripWidth = width - margin * 2;
  const totalBytes = spec.segments.reduce((sum, segment) => sum + segment.bytes, 0);
  const pixelsPerByte = stripWidth / totalBytes;
  const warnings: string[] = [];
  const offsets = segmentOffsets(spec.segments);

  builder.text('memory.eyebrow', 'memory', 'annotation', point(stripX, 24), 'ORTHOGRAPHIC MEMORY LAYOUT · LOW → HIGH ADDRESS', {
    size: narrow ? 8.5 : 10,
    mono: true,
    weight: 700,
    uppercase: true,
  });
  builder.text('memory.type', 'memory.type', 'structure', point(stripX, 51), fitInline(spec.typeName, narrow ? 25 : 46), {
    size: narrow ? 18 : 21,
    mono: true,
    weight: 700,
  });
  builder.text('memory.size', 'memory.type', 'annotation', point(width - stripX, 49), `${totalBytes} B · ${spec.wordBytes} B WORD`, {
    size: narrow ? 8.5 : 10,
    mono: true,
    weight: 700,
    align: 'end',
    uppercase: true,
  });
  builder.line('memory.direction', 'memory', 'dimension', point(stripX, 76), point(width - stripX, 76), { markerEnd: true });
  builder.text('memory.base-address', memoryOffsetId(0), 'annotation', point(stripX, 94), spec.baseAddress, {
    size: narrow ? 8.5 : 10,
    mono: true,
    weight: 700,
  });
  builder.text('memory.end-address', memoryOffsetId(totalBytes), 'annotation', point(width - stripX, 94), addressAt(spec.baseAddress, totalBytes), {
    size: narrow ? 8.5 : 10,
    mono: true,
    weight: 700,
    align: 'end',
  });

  builder.rect('memory.strip.frame', 'memory', 'structure', box(stripX, STRIP_TOP, stripWidth, STRIP_HEIGHT));

  // One subtle grid line per byte makes proportional widths inspectable rather
  // than merely decorative. The segment boundaries below carry exact offsets.
  for (let byte = 1; byte < totalBytes; byte += 1) {
    const x = stripX + byte * pixelsPerByte;
    builder.line(`memory.byte.${byte}`, 'memory.byte-grid', 'grid', point(x, STRIP_TOP), point(x, STRIP_TOP + STRIP_HEIGHT));
  }

  spec.segments.forEach((segment, index) => {
    const offset = offsets[index]!;
    const semanticId = memorySegmentId(segment.id);
    const x = stripX + offset * pixelsPerByte;
    const segmentWidth = segment.bytes * pixelsPerByte;
    const bounds = box(x, STRIP_TOP, segmentWidth, STRIP_HEIGHT);
    const role = roleForSegment(segment);
    const structuralLabel = segment.kind === 'padding'
      ? segment.label.replace(/^implicit\s+/i, '')
      : segment.label;
    const display = segmentWidth >= 66
      ? fitInline(structuralLabel, Math.max(5, Math.floor((segmentWidth - 12) / (narrow ? 5.2 : 5.8))))
      : String(index + 1).padStart(2, '0');

    builder.rect(`${semanticId}.storage`, semanticId, role, bounds, {
      label: accessibleSegmentLabel(spec, segment, offset),
      interactive: true,
    });
    builder.text(`${semanticId}.label`, semanticId, segment.kind === 'padding' ? 'padding' : 'structure', point(x + segmentWidth / 2, STRIP_TOP + 32), display, {
      size: segmentWidth >= 66 ? (narrow ? 9 : 10) : 9,
      mono: true,
      weight: 700,
      align: 'middle',
    });
    builder.text(`${semanticId}.bytes`, semanticId, 'annotation', point(x + segmentWidth / 2, STRIP_TOP + 53), `${segment.bytes} B`, {
      size: narrow ? 8 : 9,
      mono: true,
      weight: 700,
      align: 'middle',
    });
    builder.anchor(`${semanticId}.start`, semanticId, point(x, STRIP_TOP + STRIP_HEIGHT / 2), 'left');
    builder.anchor(`${semanticId}.center`, semanticId, point(x + segmentWidth / 2, STRIP_TOP + STRIP_HEIGHT / 2));
    builder.anchor(`${semanticId}.end`, semanticId, point(x + segmentWidth, STRIP_TOP + STRIP_HEIGHT / 2), 'right');
    builder.relation(`${semanticId}.occupies`, 'occupies-bytes', 'memory.type', semanticId, `${segment.label} occupies offsets ${offset}…${offset + segment.bytes - 1}`);
    if (index > 0) {
      builder.relation(`${semanticId}.follows`, 'follows-in-memory', memorySegmentId(spec.segments[index - 1]!.id), semanticId, `${segment.label} begins at offset ${offset}`);
    }
  });

  const boundaries = [...offsets, totalBytes];
  const railY = STRIP_TOP + STRIP_HEIGHT + 22;
  builder.line('memory.offset.rail', 'memory.offsets', 'dimension', point(stripX, railY), point(width - stripX, railY));
  boundaries.forEach((offset, index) => {
    const x = stripX + offset * pixelsPerByte;
    const semanticId = memoryOffsetId(offset);
    const isEdge = index === 0 || index === boundaries.length - 1;
    builder.line(`${semanticId}.tick`, semanticId, 'dimension', point(x, STRIP_TOP + STRIP_HEIGHT + 4), point(x, railY + (index % 2 === 0 ? 7 : 13)));
    builder.text(`${semanticId}.label`, semanticId, 'annotation', point(x, railY + (index % 2 === 0 ? 20 : 32)), `+${offset}`, {
      size: narrow ? 7.5 : 9,
      mono: true,
      weight: isEdge ? 700 : 500,
      align: offset === 0 ? 'start' : offset === totalBytes ? 'end' : 'middle',
    });
    builder.anchor(`${semanticId}.at`, semanticId, point(x, STRIP_TOP + STRIP_HEIGHT), 'down');
  });

  const ledgerTop = narrow ? 248 : 252;
  const columns = narrow ? 2 : 3;
  const columnGap = narrow ? 7 : 10;
  const cardWidth = (stripWidth - columnGap * (columns - 1)) / columns;
  const rows = Math.ceil(spec.segments.length / columns);
  const availableHeight = height - ledgerTop - 18;
  const rowGap = 7;
  const cardHeight = Math.min(62, (availableHeight - rowGap * (rows - 1)) / rows);

  spec.segments.forEach((segment, index) => {
    const offset = offsets[index]!;
    const semanticId = memorySegmentId(segment.id);
    const column = index % columns;
    const row = Math.floor(index / columns);
    const x = stripX + column * (cardWidth + columnGap);
    const y = ledgerTop + row * (cardHeight + rowGap);
    const cardBounds = box(x, y, cardWidth, cardHeight);
    const labelBudget = Math.max(7, Math.floor((cardWidth - 37) / (narrow ? 5.1 : 5.6)));
    const displayLabel = compactLabel(segment.label, labelBudget);
    const address = addressAt(spec.baseAddress, offset);
    const byteEnd = offset + segment.bytes - 1;

    builder.rect(`${semanticId}.card`, semanticId, roleForSegment(segment), cardBounds, {
      radius: 4,
      label: accessibleSegmentLabel(spec, segment, offset),
      interactive: true,
    });
    builder.text(`${semanticId}.ordinal`, semanticId, 'annotation', point(x + 9, y + 17), String(index + 1).padStart(2, '0'), {
      size: 8,
      mono: true,
      weight: 700,
    });
    builder.text(`${semanticId}.card-label`, semanticId, segment.kind === 'padding' ? 'padding' : 'structure', point(x + 30, y + 17), displayLabel, {
      size: narrow ? 8 : 9,
      mono: true,
      weight: 700,
    });
    builder.text(`${semanticId}.range`, semanticId, 'annotation', point(x + 9, y + 34), `+${offset}…+${byteEnd} · ${segment.bytes} B`, {
      size: narrow ? 7.5 : 8.5,
      mono: true,
      weight: 700,
    });
    builder.text(`${semanticId}.address`, semanticId, 'annotation', point(x + 9, y + 50), fitInline(address, Math.max(10, Math.floor((cardWidth - 18) / (narrow ? 4.7 : 5.2)))), {
      size: narrow ? 7.5 : 8.5,
      mono: true,
    });
    builder.hit(semanticId, cardBounds);
    builder.relation(`${semanticId}.starts-at`, 'starts-at-address', semanticId, memoryOffsetId(offset), address);

    if (displayLabel !== segment.label) warnings.push(`Segment label compacted: ${segment.id}`);
  });

  if (pixelsPerByte < 9) warnings.push('Byte cells use the dense orthographic grid policy at this width.');
  if (cardHeight < 50) warnings.push('Segment cards are approaching the touch-density floor.');

  const active = spec.focusSegmentId ? [spec.focusSegmentId] : ['memory'];
  active.push(...(spec.emphasis ?? []));

  return finishFigure(spec, builder, {
    title: 'A struct is an ordered span of addressed bytes',
    subtitle: 'The byte map makes offsets, alignment padding, and field extent explicit.',
    state: spec.focusSegmentId ? `focus:${spec.focusSegmentId}` : 'overview',
    active,
    warnings: unique(warnings),
  });
}

export function validateMemoryLayout(spec: MemoryLayoutSpec): void {
  validateViewport(spec.viewport.width, spec.viewport.height);
  if (!spec.typeName.trim()) throw new Error('memory-layout: typeName is required');
  if (!spec.provenance.trim()) throw new Error('memory-layout: provenance is required');
  if (!/^0x[0-9a-f]+$/i.test(spec.baseAddress)) throw new Error('memory-layout: baseAddress must be hexadecimal');
  if (!Number.isInteger(spec.wordBytes) || spec.wordBytes <= 0) throw new Error('memory-layout: wordBytes must be a positive integer');
  if (spec.segments.length < 1 || spec.segments.length > 8) throw new Error('memory-layout: require 1…8 segments');

  const ids = spec.segments.map((segment) => segment.id);
  if (new Set(ids).size !== ids.length) throw new Error('memory-layout: segment IDs must be unique');
  for (const segment of spec.segments) {
    if (!/^[a-z][a-z0-9-]*$/.test(segment.id)) throw new Error(`memory-layout: invalid segment id ${segment.id}`);
    if (!segment.label.trim()) throw new Error(`memory-layout: segment ${segment.id} requires a label`);
    if (!Number.isInteger(segment.bytes) || segment.bytes <= 0) throw new Error(`memory-layout: segment ${segment.id} bytes must be a positive integer`);
    if (segment.kind === 'padding' && segment.value !== undefined) throw new Error(`memory-layout: padding segment ${segment.id} cannot carry a value`);
  }
  const totalBytes = spec.segments.reduce((sum, segment) => sum + segment.bytes, 0);
  if (totalBytes > 64) throw new Error('memory-layout: the orthographic learning view supports at most 64 bytes');

  const semanticIds = new Set(ids.map(memorySegmentId));
  if (spec.focusSegmentId && !semanticIds.has(spec.focusSegmentId)) {
    throw new Error(`memory-layout: focus segment ${spec.focusSegmentId} does not exist`);
  }
}

function segmentOffsets(segments: MemorySegment[]): number[] {
  let offset = 0;
  return segments.map((segment) => {
    const result = offset;
    offset += segment.bytes;
    return result;
  });
}

function roleForSegment(segment: MemorySegment): ShapeRole {
  if (segment.kind === 'padding') return 'padding';
  if (segment.kind === 'header') return 'header';
  return 'record';
}

function addressAt(baseAddress: string, offset: number): string {
  const width = baseAddress.slice(2).length;
  const value = BigInt(baseAddress) + BigInt(offset);
  return `0x${value.toString(16).padStart(width, '0')}`;
}

function accessibleSegmentLabel(spec: MemoryLayoutSpec, segment: MemorySegment, offset: number): string {
  const value = segment.value === undefined ? '' : `, value ${String(segment.value)}`;
  return `${segment.label}, ${segment.kind}, ${segment.bytes} bytes, offsets ${offset} through ${offset + segment.bytes - 1}, address ${addressAt(spec.baseAddress, offset)}${value}`;
}

function compactLabel(value: string, max: number): string {
  if (value.length <= max) return value;
  const words = value.trim().split(/[-\s]+/);
  if (words.length > 1) {
    const first = words[0]!;
    const last = words.at(-1)!;
    const tailRoom = max - Math.min(first.length, Math.ceil(max * 0.48)) - 1;
    if (tailRoom >= 2) return `${first.slice(0, max - tailRoom - 1)}…${last.slice(-tailRoom)}`;
  }
  const head = Math.max(2, Math.ceil((max - 1) * 0.58));
  return `${value.slice(0, head)}…${value.slice(-(max - head - 1))}`;
}

function fitInline(value: string, max: number): string {
  if (value.length <= max) return value;
  if (max <= 3) return value.slice(0, max);
  return `${value.slice(0, max - 1)}…`;
}

function unique(values: string[]): string[] {
  return [...new Set(values)];
}
